import { Accordion } from "@/components/ui/accordion";
import { Calendar, MapPin, Users, FileText } from "lucide-react";
import { TimeSection } from "./TimeSection";
import { LocationSelector } from "./LocationSelector";
import { DateSection } from "./sections/DateSection";
import { DetailsSection } from "./sections/DetailsSection";
import { AccordionSection } from "./sections/AccordionSection";
import { GuestAccordion } from "./GuestAccordion";
import { useValidateEventForm } from "./hooks/useFormValidation";
import { ContentGenerationSection } from "./sections/ContentGenerationSection";
import { useSectionValidation } from "./hooks/useSectionValidation";
import { EventImage } from "./image-upload/imageUploadUtils";
import { EventFormFieldsProps } from "./types/form-types";

export const EventFormFields = ({
  title,
  setTitle,
  description,
  setDescription,
  date,
  setDate,
  preferredDates,
  setPreferredDates,
  preferredTimeOfDay,
  setPreferredTimeOfDay,
  eventLocation,
  setEventLocation,
  locationType,
  setLocationType,
  titleError,
  setTitleError,
  imageUrl,
  setImageUrl,
  imageUrls = [],
  setImageUrls = () => {},
  preferredVenues,
  setPreferredVenues,
  locationNote,
  setLocationNote,
  guestList,
  setGuestList,
  validationErrors,
  eventId,
  eventImages,
  setEventImages,
  eventDeadline,
  setEventDeadline
}: EventFormFieldsProps) => {
  const {
    validateForm,
    isSubmitDisabled
  } = useValidateEventForm(title, preferredDates, preferredTimeOfDay, locationType, preferredVenues, locationNote);
  const {
    isWhenSectionValid,
    isWhereSectionValid,
    isDetailsSectionValid,
    canGenerateDescription
  } = useSectionValidation(title, preferredDates, preferredTimeOfDay, locationType, preferredVenues, locationNote);

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
    if (titleError) setTitleError("");
  };

  const handleDateSelect = (dates: Date[] | undefined) => {
    if (!dates) {
      setPreferredDates([]);
      return;
    }
    setPreferredDates(dates);
  };

  const handleGeneratedContent = (newDescription: string, newTitle: string) => {
    setDescription(newDescription);
    setTitle(newTitle);
  };

  return <Accordion type="single" className="space-y-6 pt-4" defaultValue="when" collapsible>
      <AccordionSection value="when" title="When" icon={Calendar} isValid={isWhenSectionValid} hasError={!!(validationErrors?.dates || validationErrors?.timeOfDay)}>
        <div className="space-y-4">
          <DateSection preferredDates={preferredDates} onDateSelect={handleDateSelect} error={validationErrors?.dates} />
          <TimeSection selectedTimes={preferredTimeOfDay} onTimeSelect={setPreferredTimeOfDay} error={validationErrors?.timeOfDay} />
        </div>
      </AccordionSection>

      <AccordionSection value="where" title="Where" icon={MapPin} isValid={isWhereSectionValid} hasError={!!validationErrors?.location}>
        <LocationSelector locationType={locationType} location={eventLocation} preferredVenues={preferredVenues} locationNote={locationNote} onLocationTypeChange={setLocationType} onLocationChange={setEventLocation} onPreferredVenuesChange={setPreferredVenues} onLocationNoteChange={setLocationNote} error={validationErrors?.location} />
      </AccordionSection>

      <AccordionSection value="who" title="Who" icon={Users} isValid={true} extraContent={guestList && guestList.length > 0 && <span className="text-sm font-normal text-muted-foreground ml-2">
              ({guestList.length} {guestList.length === 1 ? 'guest' : 'guests'})
            </span>}>
        <div className="px-0">
          {setGuestList && <GuestAccordion guestList={guestList || []} onGuestListChange={setGuestList} />}
        </div>
      </AccordionSection>

      <AccordionSection value="details" title="Details" icon={FileText} isValid={isDetailsSectionValid} hasError={!!validationErrors?.title} extraContent={<ContentGenerationSection preferredDates={preferredDates} preferredTimeOfDay={preferredTimeOfDay} locationType={locationType} preferredVenues={preferredVenues} locationNote={locationNote} guestList={guestList} canGenerate={canGenerateDescription} onSuccess={handleGeneratedContent} />}>
        <div className="px-0">
          <DetailsSection title={title} description={description} titleError={validationErrors?.title || titleError} imageUrl={imageUrl} imageUrls={imageUrls} onTitleChange={handleTitleChange} onDescriptionChange={e => setDescription(e.target.value)} setImageUrl={setImageUrl} setImageUrls={setImageUrls} eventId={eventId} eventImages={eventImages} setEventImages={setEventImages} eventDeadline={eventDeadline} onDeadlineChange={setEventDeadline} />
        </div>
      </AccordionSection>
    </Accordion>;
};
